import { CATEGORY_KEYS as _CK } from '../lib/categories';
import { useAuth } from './AuthContext';
import { useBoards } from './BoardContext';

export const CATEGORY_KEYS = ['S', 'Q', 'P', 'D', 'C', 'H', 'I'];

/**
 * Returns permission helpers for the current active board & user.
 *   isAdmin        — global admin
 *   isBoardOwner   — owner_email matches user email
 *   isBoardMember  — appears in team_members
 *   canEdit(cat)   — can create/update issues + add analyses in that category
 *   allowedCategories — list of category keys the user can edit
 *   canCloseIssues — can close a non-green issue (admin or board owner)
 *   canChangeColor — can change an issue's color once it already exists (admin only)
 */
export function usePermissions() {
  const { user } = useAuth();
  const { activeBoard } = useBoards();

  const email = user?.email || '';
  const isAdmin = user?.role === 'admin';
  const isBoardOwner = !!activeBoard && activeBoard.owner_email === email;

  // Find team member entry
  let memberEntry = null;
  const members = activeBoard?.team_members || [];
  for (const m of members) {
    if (typeof m === 'string') {
      if (m === email) memberEntry = { email: m, role: 'user', assigned_categories: CATEGORY_KEYS };
    } else if (m?.email === email) {
      memberEntry = m;
    }
  }
  const isBoardMember = !!memberEntry;

  const canEdit = (categoryKey) => {
    if (!user || !activeBoard) return false;
    if (isAdmin) return true;
    if (isBoardOwner) return true;
    if (!memberEntry) return false;
    if (memberEntry.role === 'owner') return true;
    return (memberEntry.assigned_categories || []).includes(categoryKey);
  };

  let allowedCategories = [];
  if (isAdmin || isBoardOwner || memberEntry?.role === 'owner') {
    allowedCategories = CATEGORY_KEYS;
  } else if (memberEntry) {
    allowedCategories = memberEntry.assigned_categories || [];
  }

  return {
    user,
    isAdmin,
    isBoardOwner,
    isBoardMember,
    memberEntry,
    canEdit,
    allowedCategories,
    canManageTeam: isAdmin || isBoardOwner,
    canManageBoardSettings: isAdmin || isBoardOwner,
    canCloseIssues: isAdmin || isBoardOwner,
    canChangeColor: isAdmin,
  };
}
